import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2, CreditCard, Plus, X } from "lucide-react";
import {
  useGetSavedCards,
  useDeleteCard,
  useAddCard,
} from "@/api/hooks/use-query-api";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { Button } from "@/components/ui/button";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";
import type { SavedCard } from "@/api/models/api-types";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/features/toast";
import { CardFields } from "@/features/cards/components/CardFields";
import { useCardForm } from "@/features/cards/hooks/use-card-form";

export const MySavedCardsTab: React.FC = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const { data: savedCardsData, isLoading } = useGetSavedCards();
  const { mutateAsync: deleteCard, isPending: isDeleting } = useDeleteCard();
  const { mutateAsync: addCard, isPending: isAdding } = useAddCard();

  const savedCards: SavedCard[] = savedCardsData?.data?.cards ?? [];

  const [deletingCard, setDeletingCard] = React.useState<SavedCard | null>(
    null,
  );
  const [showAddForm, setShowAddForm] = React.useState(false);

  // Kart formu — checkout ile aynı alanlar, aynı kurallar, aynı payload
  // yardımcıları. Burada CVC yok (`save` modu): `POST /cards` istemiyor.
  const form = useCardForm("save");

  const handleCancelAdd = () => {
    setShowAddForm(false);
    form.reset();
  };

  const handleAddCard = async () => {
    form.touchAll();
    if (!form.isValid) return;

    try {
      await addCard(form.toSaveCardPayload());
      success(t("cards.addCardSuccess"));
      setShowAddForm(false);
      form.reset();
    } catch {
      error(t("cards.addCardError"));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCard) return;
    try {
      await deleteCard(deletingCard.cardToken);
      success(t("cards.deleteCardSuccess"));
    } catch {
      error(t("cards.deleteCardError"));
    } finally {
      setDeletingCard(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("cards.title")}
          </h2>
          <p className="text-xs md:text-sm text-text-tertiary dark:text-gray-400 mt-1">
            {t("cards.subtitle")}
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          size="sm"
          disabled={showAddForm}
          className="bg-[#6172f3] hover:bg-[#4c5fd5] text-white flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          {t("cards.addCard")}
        </Button>
      </div>

      {/* Add Card Form */}
      {showAddForm && (
        <div className="mb-6 border border-[#6172f3]/30 rounded-[12px] p-4 md:p-5 bg-[#f5f8ff] dark:bg-blue-950/10 space-y-4">
          <h3 className="text-sm md:text-base font-semibold text-text-primary dark:text-gray-100">
            {t("cards.addCardTitle")}
          </h3>

          <CardFields form={form} idPrefix="savedCards" />

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button
              variant="outline"
              onClick={handleCancelAdd}
              className="flex-1 border-[#e3e8ef] dark:border-gray-700 text-text-primary dark:text-gray-100"
              disabled={isAdding}
            >
              {t("cards.cancelAdd")}
            </Button>
            <Button
              onClick={handleAddCard}
              disabled={isAdding}
              className="flex-1 bg-[#6172f3] hover:bg-[#4c5fd5] text-white"
            >
              {isAdding ? <LoadingSpinner size="sm" /> : t("cards.saveCard")}
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {savedCards.length === 0 && !showAddForm && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 rounded-full bg-[#f5f8ff] dark:bg-gray-800 flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-[#6172f3]" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary dark:text-gray-100">
              {t("cards.noCards")}
            </p>
            <p className="text-xs text-text-tertiary dark:text-gray-400 mt-1 max-w-xs">
              {t("cards.noCardsDescription")}
            </p>
          </div>
        </div>
      )}

      {/* Cards List */}
      {savedCards.length > 0 && (
        <div className="flex flex-col gap-3">
          {savedCards.map((card) => {
            const isExpired = card.cardExpiryStatus === "EXPIRED";
            return (
              <div
                key={card.cardToken}
                className="flex items-center justify-between gap-4 p-3 md:p-4 border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] bg-white dark:bg-gray-800"
              >
                {/* Left: Brand + Info */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-8 flex-shrink-0 bg-[#f9fafb] dark:bg-gray-700 border border-[#e3e8ef] dark:border-gray-600 rounded-[6px] flex items-center justify-center">
                    {card.cardAssociation ? (
                      <PaymentBrandLogo
                        brand={card.cardAssociation}
                        className="w-8 h-5"
                      />
                    ) : (
                      <CreditCard className="w-5 h-5 text-gray-400" />
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      {/* Alias önce: iki kartı ayırmak için kullanıcının kendi verdiği ad markadan daha anlamlı */}
                      <span className="text-sm font-semibold text-text-primary dark:text-gray-100 truncate">
                        {card.cardAlias ||
                          card.cardBrand ||
                          card.cardAssociation ||
                          `•••• ${card.lastFourDigits}`}
                      </span>
                      {isExpired && (
                        <span className="text-[10px] font-medium text-red-500 bg-red-50 dark:bg-red-950/30 dark:text-red-400 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {t("cards.cardExpired")}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-tertiary dark:text-gray-400">
                      {[card.cardBankName, `•••• ${card.lastFourDigits}`]
                        .filter(Boolean)
                        .join(" • ")}
                    </span>
                  </div>
                </div>

                {/* Right: Delete Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeletingCard(card)}
                  className="flex-shrink-0 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingCard}
        onOpenChange={(open) => !open && setDeletingCard(null)}
      >
        <DialogContent
          open={!!deletingCard}
          className="max-w-md p-6 rounded-[20px] bg-white dark:bg-gray-900"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="text-[20px] font-semibold leading-[28px] text-[#121926] dark:text-gray-100">
                {t("checkout.deletePaymentMethod")}
              </h2>
              <button
                onClick={() => setDeletingCard(null)}
                disabled={isDeleting}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-[14px] font-normal leading-[22px] text-[#364152] dark:text-gray-400">
              {t("checkout.deletePaymentMethodConfirmation")}
            </p>

            {deletingCard && (
              <div className="bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] p-3 flex items-center gap-3">
                <div className="w-[57px] h-[40px] bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded flex items-center justify-center">
                  <PaymentBrandLogo
                    brand={(
                      deletingCard.cardBrand ||
                      deletingCard.cardAssociation ||
                      "card"
                    ).toLowerCase()}
                    className="w-10 h-6"
                  />
                </div>
                <div className="flex flex-col gap-0.5 flex-1">
                  {deletingCard.cardAlias && (
                    <p className="text-[12px] font-medium text-[#6172f3]">
                      {deletingCard.cardAlias}
                    </p>
                  )}
                  <p className="text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                    **** **** **** {deletingCard.lastFourDigits}
                  </p>
                  <p className="text-[12px] font-normal text-[#697586] dark:text-gray-400">
                    {deletingCard.cardBankName || deletingCard.cardBrand}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 mt-6">
            <Button
              variant="outline"
              onClick={() => setDeletingCard(null)}
              disabled={isDeleting}
              className="flex-1 bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 text-[#414651] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-[8px] px-[14px] py-[10px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="flex-1 bg-[#f04438] hover:bg-[#d92d20] dark:bg-[#f04438] dark:hover:bg-[#d92d20] text-white rounded-[10px] px-4 py-[10px] font-semibold text-[14px] leading-[20px]"
            >
              {isDeleting ? t("common.deleting") : t("common.delete")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
